import React, { useContext, useState, useEffect } from "react";
import { BaseModal } from "../ui/BaseModal.tsx";
import { Box } from "@mui/material";
import { ThemeContext } from "../ThemeContext";
import AlertMessage from "../ui/AlertMessage.tsx";
import Pagination from "../ui/Pagination.tsx";
import { useProfileWizard } from "../../hooks/useProfileWizard.ts";
import { useProfileData } from "../../hooks/useProfileData.ts";
import { useProfileOperations } from "../../hooks/useProfileOperations.ts";
import { useProfileUI } from "../../hooks/useProfileUI.ts";
import ProfileWizardSteps from "./profileModal/ProfileWizardSteps.tsx";
import ProfileExistingList from "./profileModal/ProfileExistingList.tsx";
import ProfileFormWizard from "./profileModal/ProfileFormWizard.tsx";
import ProfilePreview from "./profileModal/ProfilePreview.tsx";
import ProfileWizardNavigation from "./profileModal/ProfileWizardNavigation.tsx";
import { generateProfileSummary } from "../../utils/profileUtils.ts";
import { UI_CONFIG } from "../../utils/profileConstants.ts";

interface StepperProfileModalProps {
  onClose: () => void;
  isOpen: boolean;
}

const StepperProfileModal = ({ onClose, isOpen }: StepperProfileModalProps) => {
  const { isDarkMode } = useContext(ThemeContext);
  const [textConfig, setTextConfig] = useState<any>({});

  const fetchTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setTextConfig(data);
    } catch (error) {
      console.error("Error loading text config:", error);
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);

  const {
    activeStep,
    showPreview,
    error,
    steps,
    handleNext,
    handleBack,
    goToStep,
    resetWizard,
    canGoNext,
    canGoBack,
    getNextButtonText,
    setError
  } = useProfileWizard();

  const {
    profileData,
    availableCollections,
    existingProfiles,
    isLoading: dataLoading,
    pagination,
    totalPages,
    totalProfiles,
    searchProfileId,
    setProfileData,
    resetProfileData,
    loadProfileForEdit,
    handleCollectionToggle,
    fetchProfiles,
    handlePageChange,
    handleSearchByProfile,
    clearSearch,
    getDefaultCollection,
    isDefaultCollection,
    getUniqueCollections,
    formatProfileForAPI
  } = useProfileData(isOpen);

  const {
    isLoading: operationsLoading,
    error: operationsError,
    handleCreateProfile,
    handleUpdateProfile,
    handleDeleteProfile,
    validateProfileData,
    checkProfileNameExists,
    clearError,
    setError: setOperationsError
  } = useProfileOperations();

  const {
    profilesExpanded,
    adjustmentsExpanded,
    instructionsExpanded,
    editMode,
    profileToEdit,
    alertConfig,
    handleEditProfile,
    handleCancelEdit,
    exitEditMode,
    setAlertConfig,
    toggleProfilesExpanded,
    toggleAdjustmentsExpanded,
    toggleInstructionsExpanded,
    resetUIStates
  } = useProfileUI();

  const isLoading = dataLoading || operationsLoading;

  const combinedError = error || operationsError;

  const handleCompleteWizard = async (): Promise<void> => {
    const validation = validateProfileData(profileData);
    if (!validation.isValid) {
      setError(validation.errors[0]);
      return;
    }

    if (!editMode && checkProfileNameExists(profileData.profileName, existingProfiles)) {
      setError(textConfig.STEPPER_PROFILE_MODAL_PROFILE_NAME_EXISTS_ERROR);
      return;
    }

    const onSuccess = () => {
      fetchProfiles(1, 10);
      // Reset to first view instead of closing modal
      resetWizard();
      resetProfileData();
      resetUIStates();
      clearError();
    };

    if (editMode && profileToEdit) {
      await handleUpdateProfile(
        profileToEdit,
        profileData,
        formatProfileForAPI,
        onSuccess,
        setAlertConfig
      );
    } else {
      await handleCreateProfile(
        profileData,
        formatProfileForAPI,
        onSuccess,
        setAlertConfig
      );
    }
  };

  const handleWizardNext = (): void => {
    handleNext(profileData, handleCompleteWizard);
  };

  const handleWizardBack = (): void => {
    handleBack();
  };

  const handleStartEdit = (profile: any): void => {
    handleEditProfile(profile);
    loadProfileForEdit(profile);
    resetWizard();
  };

  const handleCancelEditMode = (): void => {
    handleCancelEdit();
    resetProfileData();
    resetWizard();
    clearError();
  };

  const handleDeleteProfileAction = async (profileId: string): Promise<void> => {
    const onSuccess = () => {
      fetchProfiles();
    };

    await handleDeleteProfile(profileId, onSuccess, setAlertConfig);
  };

  const handleCloseModal = (): void => {
    resetWizard();
    resetProfileData();
    resetUIStates();
    clearError();
    onClose();
  };

  const handleClearError = (): void => {
    setError("");
    clearError();
  };

  const profileSummary = showPreview ? generateProfileSummary(profileData) : null;

  const getModalTitle = () => {
    return editMode 
      ? textConfig.STEPPER_PROFILE_MODAL_EDIT_TITLE 
      : textConfig.STEPPER_PROFILE_MODAL_MANAGE_TITLE;
  };

  return (
    <BaseModal
      title={getModalTitle()}
      onClose={handleCloseModal}
      isOpen={isOpen}
    >
      <Box sx={{ p: 2 }}>
        <ProfileWizardSteps
          steps={steps}
          activeStep={activeStep}
          onStepClick={goToStep}
          isDarkMode={isDarkMode}
        />

        {showPreview ? (
          <ProfilePreview
            profileSummary={profileSummary!}
            profileData={profileData}
            isDarkMode={isDarkMode}
            editMode={editMode}
          />
        ) : (
          <ProfileFormWizard
            activeStep={activeStep}
            profileData={profileData}
            availableCollections={availableCollections}
            isLoading={isLoading}
            isDarkMode={isDarkMode}
            adjustmentsExpanded={adjustmentsExpanded}
            setAdjustmentsExpanded={toggleAdjustmentsExpanded}
            instructionsExpanded={instructionsExpanded}
            setInstructionsExpanded={toggleInstructionsExpanded}
            handleCollectionToggle={handleCollectionToggle}
            setProfileData={setProfileData}
            isDefaultCollection={isDefaultCollection}
            getUniqueCollections={getUniqueCollections}
            isEditMode={editMode}
            error={combinedError}
            onClearError={handleClearError}
            onNext={handleWizardNext}
            canGoNext={canGoNext(profileData)}
            nextButtonText={getNextButtonText(isLoading, editMode)}
          />
        )}

        {combinedError && (
          <Box sx={{ 
            color: "#FF3D3D", 
            mt: 1, 
            mb: 2, 
            textAlign: "center", 
            fontSize: { xs: "12px", sm: "14px" }
          }}>
            {combinedError}
          </Box>
        )}

        {alertConfig.open && (
          <AlertMessage
            type={alertConfig.type}
            message={alertConfig.message}
            onClose={() => setAlertConfig({ ...alertConfig, open: false })}
          />
        )}

        <ProfileWizardNavigation
          activeStep={activeStep}
          showPreview={showPreview}
          editMode={editMode}
          isLoading={isLoading}
          canGoNext={canGoNext(profileData)}
          canGoBack={canGoBack()}
          nextButtonText={getNextButtonText(isLoading, editMode)}
          onNext={handleWizardNext}
          onBack={handleWizardBack}
          onCancel={handleCancelEditMode}
          isDarkMode={isDarkMode}
        />

        {!editMode && activeStep === 0 && (
          <>
            <ProfileExistingList
              profiles={existingProfiles}
              isLoading={isLoading}
              expanded={profilesExpanded}
              onToggleExpanded={toggleProfilesExpanded}
              onEditProfile={handleStartEdit}
              onDeleteProfile={handleDeleteProfileAction}
              isDarkMode={isDarkMode}
              totalProfiles={totalProfiles}
              searchProfileId={searchProfileId}
              onSearchByProfile={handleSearchByProfile}
              onClearSearch={clearSearch}
            />
            
            {(existingProfiles.length > 0 || (pagination && pagination.currentPage > 1)) && pagination && totalPages && (
              <div className="mt-6">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={totalPages}
                  isLoading={pagination.isLoading || isLoading}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </Box>
    </BaseModal>
  );
};

export default StepperProfileModal;